"use client";

import React, { useState, useId, useRef } from "react";
import styles from "./MunicipalityServicesBlock.module.css";
import Image from "@/components/atoms/image";
import { motion, AnimatePresence } from "framer-motion";
import BupiLinkButton from "@/components/atoms/linkButton";
import { useTranslations } from "next-intl";
import { IServiceAvailabilityBlock, IServiceAvailabilityAction } from "@/page-builder/fragments/ServiceAvailabilityBlock/types";
import { RichText } from "@payloadcms/richtext-lexical/react";
import { renderRichTextAsHTML } from "@/components/molecules/Heading";
import { DropdownOption, DropdownOptionProps, DropdownSection, InputSelect } from "@ama-pt/agora-design-system";

export const MunicipalityServicesBlock: React.FC<
  IServiceAvailabilityBlock
> = ({ title, description, yes, yesCp, yesSg, yesNa, municipalities = [], defaultImage }) => {
  console.log("DDD - ", yes, yesCp, yesSg, yesNa);
  const t = useTranslations('MunicipalityServicesBlock');
  const generatedId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const [selected, setSelected] = useState<string>("");
  
  const selectedMunicipality = municipalities?.find((m) => m.municipality === selected);

  const getActionData = (municipality: any): IServiceAvailabilityAction => {
    if (municipality.cadastroPredial && yesCp) return yesCp;
    if (municipality.sirgic && yesSg) return yesSg;
    if (municipality.nenhumDosAnteriores && yesNa) return yesNa;
    
    return yes;
  };

  const onInputSelectChange = (selectedOptions: DropdownOptionProps[]) => {
    const selectedValue = selectedOptions.length > 0 ? selectedOptions[0].value : "";
    setSelected(selectedValue === "none" ? "" : selectedValue as string);

    if (selectedValue && selectedValue !== "none" && sectionRef.current) {
      const currentScrollY = window.scrollY;
      const targetScrollY = Math.max(0, currentScrollY - 200);
      
      window.scrollTo({
        top: targetScrollY,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      ref={sectionRef}
      className={`
        relative
        mb-64 xl:mb-96
        overflow-visible
        min-h-[612px]
        py-[114px] px-16 xl:px-32
        flex items-center justify-center
        text-[#2b363c]
      `}
      aria-labelledby="discover-hero-title"
    >
      <div 
        className={`${styles["discover-territory-hero"]} absolute inset-0 bg-secondary-200`}
      >
        <div
          className={`${styles["block-left-top"]} absolute left-0 top-0 bg-secondary-400 z-10`}
        />
        <div
          className={`${styles["block-right-bottom"]} absolute right-0 bottom-0 bg-secondary-300 z-10`}
        />
      </div>

      <div
        className="
          container
          relative z-30 
          mx-auto
          px-16 xl:px-24
          flex flex-col xl:flex-row gap-32 items-center justify-center xl:justify-between
        "
      >
        <div className="flex flex-col items-center xl:items-start border-b-2 xl:border-none border-neutral-700 pb-32 xl:pb-0">
          <div className="block xl:hidden w-[115px] h-[218px] object-contain relative">
            <Image
              src={"/assets/municipality/boneco_not_selected.svg"}
              alt="Boneco"
              fill
              className="object-contain relative"
            />
          </div>
          
          <div className="flex flex-col items-center xl:justify-center max-w-[488px] text-center">
            <div id="discover-hero-title">
              {renderRichTextAsHTML(title, false, "text-neutral-900")}
            </div>

            <div className="text-m-regular mt-16">
              <RichText data={description as any} />
            </div>

            <div className="w-full max-w-[296px] md:max-w-[384px] mt-16 relative z-50">
              <InputSelect
                placeholder={t("municipality")}
                type="text"
                onChange={onInputSelectChange}
                dropdownAriaLabel="Municipality list"
                searchable
                searchInputPlaceholder={t("searchMunicipality")}
                className="!h-[300px] min-h-80 bg-black"
                searchNoResultsText={t("noResults")}
                visibleCount={6}
              >
                <DropdownSection 
                  key={`municipality-select-${generatedId}`} 
                  label={t("municipalities")} 
                  name="municipalities"
                >
                  {municipalities?.map((m, index) => (
                    <DropdownOption
                      key={`municipality-select-${generatedId}-${index}`} 
                      value={m.municipality}
                      selected={selected === m.municipality}
                    >
                      {m.municipality}
                    </DropdownOption>
                  ))}
                </DropdownSection>
              </InputSelect>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center text-center">
          <AnimatePresence mode="wait">
            {!selectedMunicipality && (
              <motion.div
                key="boneco-lateral"
                className="hidden xl:block w-[154px] h-[292px] object-contain relative"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                {defaultImage && (
                  <Image
                    src={defaultImage.url}
                    alt={defaultImage.alt}
                    fill
                    className="object-contain relative"
                  />
                )}
              </motion.div>
            )}

            {selectedMunicipality && (
              <motion.div
                key={selectedMunicipality.municipality} 
                className="w-full flex flex-row items-center justify-between gap-32"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
              >
                {(() => {
                  const actionData = getActionData(selectedMunicipality);
                  
                  return (
                    <>
                      <div className="hidden xl:block w-[231px] h-[292px] object-contain relative">
                        {actionData?.image && (
                          <Image
                            src={actionData.image.url}
                            alt={actionData.image.alt}
                            fill
                            className="object-contain relative"
                          />
                        )}
                      </div>
                      <div className="flex-1 flex flex-col gap-24 items-center">
                        {actionData?.description && (
                          <p className="text-m-regular">
                            {actionData.description}
                          </p>
                        )}
                        {actionData?.url && actionData?.label && actionData?.label !== "N/A" && (
                          <BupiLinkButton
                            label={actionData.label}
                            link={actionData.url}
                            target={actionData.target}
                            hasIcon
                            leadingIcon="agora-line-arrow-right-circle"
                          />
                        )}
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
